<script lang="ts" setup>
import { computed, ref } from 'vue';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import useModal, { ModalTypes } from '../../composables/useModal';
import Modal from '../Modal.vue';
import Duplicate from './Duplicate.vue';
import AlreadyExists from './AlreadyExists.vue';
import Email from './Email.vue';
import Name from './Name.vue';
import InvalidGroup from './InvalidGroup.vue';
import { useLocale } from '../../composables/useLocale';
import { Conflict } from './types';
import { UserToCreate } from '../NewUser/useCreateMultipleUsers';

type Props = {
  name: ModalTypes;
  conflictedUsers: Conflict[];
  organizationUid: string;
};
const props = withDefaults(defineProps<Props>(), {
  conflictedUsers: () => [],
});

export type CompleteEvent = {
  removeUsers: UserToCreate[];
  propertyComplete: Record<string, { email?: string | undefined; name?: string | undefined }>;
  useInstead: Record<string, CustomerMgmtTypes.GetOrganizationUsersResponse['data'][number]>;
  resolvedKeys: string[];
};

type EmailUpdate = { type: 'email'; email: string; key: string };
type AlreadyExistsUpdate = { type: 'exists'; replaceUid: string | null; key: string };
type DuplicateUpdate = { type: 'duplicate'; removedUsers: UserToCreate[] | null; key: string };

type Emits = {
  (e: 'complete', event: CompleteEvent): void;
  (e: 'resolve', event: EmailUpdate | AlreadyExistsUpdate | DuplicateUpdate): void;
};
const emit = defineEmits<Emits>();

const { isOpen, close } = useModal(props.name);

const { t } = useLocale();

type ConflictExtended = Conflict & {
  onCancelAction?: () => void;
  onCancelActionLabel?: string;
  onDoneClick: () => void;
  disableCancelButton?: boolean;
  disableActionButton?: boolean;
};

const onNext = async () => {
  if (!conflictsResolved.value) {
    if (currentConflict.value) {
      if (currentConflict.value.type === 'duplicate') {
        resolvedConflictsKeys.value.push(...currentConflict.value.users.map((u: UserToCreate) => u.key));
      } else {
        resolvedConflictsKeys.value.push(currentConflict.value.user.key);
      }
      currentConflictNumber.value += 1;
      currentConflict.value = conflicts.value[currentConflictNumber.value];
    }
  } else {
    close();
  }
};

const onCreateNew = () => {
  onNext();
};

const sortNumber: Record<Conflict['type'], number> = {
  duplicate: 0,
  exists: 1,
  'invalid-group': 2,
  'invalid-email': 3,
  'invalid-name': 3,
};
const conflicts = computed(() => {
  let userConflicts = props.conflictedUsers;
  userConflicts = userConflicts.sort((a, b) => {
    return sortNumber[a.type] - sortNumber[b.type];
  });
  const conf = userConflicts.reduce((acc: ConflictExtended[], conflict: Conflict) => {
    if (['duplicate', 'invalid-email', 'invalid-name', 'invalid-group'].includes(conflict.type)) {
      acc.push({
        ...conflict,
        onCancelAction: undefined,
        onCancelActionLabel: undefined,
        onDoneClick: onNext,
        disableCancelButton: false,
        disableActionButton: false,
      });
    } else {
      acc.push({
        ...conflict,
        onCancelAction: onCreateNew,
        onCancelActionLabel: t('lcp.create_new'),
        onDoneClick: onNext,
        disableCancelButton: false,
        disableActionButton: true,
      });
    }

    return acc;
  }, new Array<ConflictExtended>());

  return conf;
});

const currentConflictNumber = ref<number>(0);
const currentConflict = ref<ConflictExtended | null>(null);
const currentComponentRef = ref();
const useInstead = ref<Record<string, CustomerMgmtTypes.GetOrganizationUsersResponse['data'][number]>>({});
const resolvedConflictsKeys = ref<string[]>([]);

const disableAction = computed(() => {
  return !currentComponentRef?.value?.isValid?.() || currentConflict.value?.disableActionButton;
});

const title = computed(() => {
  if (conflictsResolved.value) {
    return t('lcp.conflicts_resolved_title');
  }
  return t('lcp.conflict_progress', { progress: currentConflictNumber.value + 1, total: conflicts.value.length });
});

const resolves = ref<Record<string, any>>({});
const onUpdateProperty = (userKey: string, value: string | undefined, propName: 'email' | 'name' | 'groupUid') => {
  if (!resolves.value[userKey]) {
    resolves.value[userKey] = {};
  }
  resolves.value[userKey][propName] = value;
};

const removeUsers = ref<UserToCreate[]>([]);
const onDuplicateUpdate = (value: UserToCreate, status: 'add' | 'remove') => {
  const index = removeUsers.value.findIndex((e) => e.key === value.key);
  if (status === 'add' && index === -1) {
    removeUsers.value.push(value);
  } else if (status === 'remove' && index > -1) {
    removeUsers.value.splice(index, 1);
  }
};

const conflictsResolved = computed(() => {
  return currentConflictNumber.value >= conflicts.value.length;
});

const onUpdateExisting = (key: string, user: CustomerMgmtTypes.GetOrganizationUsersResponse['data'][number] | null) => {
  if (user) {
    if (currentConflict.value) {
      currentConflict.value.disableCancelButton = true;
      currentConflict.value.disableActionButton = false;
    } else {
      // should never happen since called by the existing conflict
      console.error('current conflict ref could not be found');
    }
    useInstead.value[key] = user;
  } else {
    if (currentConflict.value) {
      currentConflict.value.disableCancelButton = false;
      currentConflict.value.disableActionButton = true;
    } else {
      // should never happen since called by the existing conflict
      console.error('current conflict ref could not be found');
    }
    delete useInstead.value[key];
  }
};

const onComplete = () => {
  const complete = {
    propertyComplete: resolves.value,
    removeUsers: removeUsers.value,
    useInstead: useInstead.value,
    resolvedKeys: resolvedConflictsKeys.value,
  };

  emit('complete', complete);
  close();
};

const reset = () => {
  removeUsers.value = [];
  useInstead.value = {};
  resolves.value = {};
  resolvedConflictsKeys.value = [];
  currentConflictNumber.value = 0;
  currentConflict.value = conflicts.value[currentConflictNumber.value];
};

const isUserResolved = computed(() => {
  if (currentConflict.value) {
    const conflict = currentConflict.value;
    if ('user' in conflict) {
      return !!removeUsers.value.find((u) => u.key === conflict.user.key);
    }
  }
  return false;
});

const actionDisabled = computed(() => {
  if (conflictsResolved.value) {
    return false;
  }

  if (isUserResolved.value) {
    return false;
  }

  return disableAction.value;
});

defineExpose({
  reset,
});
</script>
<template>
  <Modal
    :name="name"
    :is-open="isOpen"
    :title="title"
    :actionLabel="conflictsResolved ? 'Close' : 'Done'"
    :action="conflictsResolved ? onComplete : currentConflict?.onDoneClick"
    :action-disabled="actionDisabled"
    :cancelAction="currentConflict?.onCancelAction"
    :cancelActionLabel="currentConflict?.onCancelActionLabel"
    :showCancelAction="!!currentConflict?.onCancelAction"
    :cancelDisabled="isUserResolved || currentConflict?.disableCancelButton"
  >
    <v-row>
      <v-col>
        <v-progress-linear
          color="primary"
          :model-value="currentConflictNumber"
          :max="conflicts.length"
        ></v-progress-linear>
      </v-col>
    </v-row>

    <v-row v-if="conflictsResolved">
      <v-col>
        <p>{{ t('lcp.conflicts_resolved_info') }}</p>
      </v-col>
    </v-row>
    <template v-if="currentConflict">
      <v-row>
        <v-col>
          <v-alert v-if="isUserResolved" type="info" variant="outlined" border="start">
            {{ t('lcp.conflict_is_resolved') }}
          </v-alert>
        </v-col>
      </v-row>
      <span :class="isUserResolved ? 'opacity-30' : ''">
        <AlreadyExists
          ref="currentComponentRef"
          v-if="currentConflict.type === 'exists'"
          :users="currentConflict.users"
          :user="currentConflict.user"
          :key="currentConflict.keys"
          @update="onUpdateExisting"
          :disabled="isUserResolved"
        />
        <Duplicate
          v-if="currentConflict.type === 'duplicate'"
          ref="currentComponentRef"
          :key="currentConflict.keys"
          :duplicate-users="currentConflict.users"
          @update="onDuplicateUpdate"
          :disabled="isUserResolved"
        />
        <Email
          v-if="currentConflict.type === 'invalid-email'"
          ref="currentComponentRef"
          :user="currentConflict.user"
          :key="currentConflict.keys"
          :modelValue="currentConflict.user.email"
          @update="(key, val) => onUpdateProperty(key, val, 'email')"
          :disabled="isUserResolved"
        />
        <Name
          v-if="currentConflict.type === 'invalid-name'"
          ref="currentComponentRef"
          :user="currentConflict.user"
          :key="currentConflict.keys"
          :modelValue="currentConflict.user.name"
          @update="(key, val) => onUpdateProperty(key, val, 'name')"
          :disabled="isUserResolved"
        />
        <InvalidGroup
          v-if="currentConflict.type === 'invalid-group'"
          ref="currentComponentRef"
          :organization-uid="organizationUid"
          :user="currentConflict.user"
          :key="currentConflict.keys"
          :modelValue="currentConflict.user.groupUid"
          @update="(key, val) => onUpdateProperty(key, val, 'groupUid')"
          :disabled="isUserResolved"
        />
      </span>
    </template>
  </Modal>
</template>
